import { useState, useEffect } from 'react'
import type { DocumentInput } from '@creanizer/types'
import { Button, Input, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Textarea, Label, Slider, Checkbox } from '@creanizer/ui'
import { Sparkles, Settings2, ChevronDown, ChevronUp, Globe, Users, FileText, BookOpen, Sword, Building, Package, Zap, Home, Upload, PenTool, Palette, Image } from 'lucide-react'
import { DocumentTypeSelector } from './DocumentTypeSelector'
import { ImageUploader } from './ImageUploader'
import { getStylesForType, getDefaultStyle } from './utils/visualStyles'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useSkillStore } from '../../../../stores/skillStore'
import { useBuildingStore } from '../../../../stores/buildingStore'
import { 
  generateWorldContextText, 
  generateCharacterContext,
  generateEnemyContext,
  generateCommunityContext,
  generateSocietyContext,
  generateItemContext
} from '@creanizer/utils'

interface DocumentFormEnhancedProps {
  input: DocumentInput
  onChange: (input: DocumentInput) => void
  onGenerate: () => void
  isGenerating: boolean
}

export function DocumentFormEnhanced({
  input,
  onChange,
  onGenerate,
  isGenerating
}: DocumentFormEnhancedProps) {
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [selectedWorldId, setSelectedWorldId] = useState<string>(input.worldContext?.worldId || '')
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>(input.characterIds || [])
  const [selectedEnemyId, setSelectedEnemyId] = useState<string>(input.enemyIds?.[0] || '')
  const [selectedCommunityId, setSelectedCommunityId] = useState<string>(input.communityIds?.[0] || '')
  const [selectedSocietyId, setSelectedSocietyId] = useState<string>(input.societyIds?.[0] || '')
  const [selectedItemId, setSelectedItemId] = useState<string>(input.itemIds?.[0] || '')
  const [selectedSkillId, setSelectedSkillId] = useState<string>(input.skillIds?.[0] || '')
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>(input.buildingIds?.[0] || '')
  const [uploadedContent, setUploadedContent] = useState<string>('')
  const [targetLength, setTargetLength] = useState<string>('')

  // inputが変更されたときに選択状態を同期
  useEffect(() => {
    console.log('[DocumentFormEnhanced] useEffect triggered')
    console.log('[DocumentFormEnhanced] input.characterIds:', input.characterIds)
    console.log('[DocumentFormEnhanced] Setting selectedCharacterIds to:', input.characterIds || [])

    setSelectedWorldId(input.worldContext?.worldId || '')
    setSelectedCharacterIds(input.characterIds || [])
    setSelectedEnemyId(input.enemyIds?.[0] || '')
    setSelectedCommunityId(input.communityIds?.[0] || '')
    setSelectedSocietyId(input.societyIds?.[0] || '')
    setSelectedItemId(input.itemIds?.[0] || '')
    setSelectedSkillId(input.skillIds?.[0] || '')
    setSelectedBuildingId(input.buildingIds?.[0] || '')

    // 文字数の同期（「約1000文字」から数値を抽出）
    if (input.contentRequirements?.length) {
      const match = input.contentRequirements.length.match(/\d+/)
      if (match) {
        setTargetLength(match[0])
      }
    } else {
      setTargetLength('')
    }
  }, [
    input.worldContext?.worldId,
    JSON.stringify(input.characterIds), // JSON.stringifyで配列の値の変化を検出
    JSON.stringify(input.enemyIds),
    JSON.stringify(input.communityIds),
    JSON.stringify(input.societyIds),
    JSON.stringify(input.itemIds),
    JSON.stringify(input.skillIds),
    JSON.stringify(input.buildingIds),
    input.contentRequirements?.length
  ])
  
  const { currentWorld, savedWorlds } = useWorldBuilderStore()
  const { characters } = useCharacterStore()
  const { enemies } = useEnemyStore()
  const { savedCommunities: communities } = useCommunityStore()
  const { savedSocieties: societies } = useSocietyStore()
  const { items } = useItemStore()
  const { skills } = useSkillStore()
  const { buildings } = useBuildingStore()
  
  const handleChange = (field: keyof DocumentInput, value: any) => {
    onChange({
      ...input,
      [field]: value
    })
  }
  
  const handleStyleChange = (field: string, value: any) => {
    onChange({
      ...input,
      style: {
        ...input.style,
        [field]: value
      }
    })
  }

  // 文字数変更のハンドラ
  const handleLengthChange = (value: string) => {
    setTargetLength(value)

    if (value === '' || value === '0') {
      // 空欄または0の場合はcontentRequirementsを削除
      const { contentRequirements, ...rest } = input
      onChange(rest)
    } else {
      // 数値がある場合は「約○○文字」形式で設定
      onChange({
        ...input,
        contentRequirements: {
          ...input.contentRequirements,
          length: `約${value}文字`
        }
      })
    }
  }

  // 画像アセット処理
  const handleAvatarUpload = (name: string, image: string) => {
    onChange({
      ...input,
      imageAssets: {
        ...input.imageAssets,
        avatars: {
          ...input.imageAssets?.avatars,
          [name]: image
        }
      }
    })
  }
  
  const handleHeaderUpload = (image: string) => {
    onChange({
      ...input,
      imageAssets: {
        ...input.imageAssets,
        headerImage: image
      }
    })
  }
  
  const handleAvatarRemove = (name: string) => {
    const newAvatars = { ...input.imageAssets?.avatars }
    delete newAvatars[name]
    onChange({
      ...input,
      imageAssets: {
        ...input.imageAssets,
        avatars: newAvatars
      }
    })
  }
  
  const handleHeaderRemove = () => {
    onChange({
      ...input,
      imageAssets: {
        ...input.imageAssets,
        headerImage: undefined
      }
    })
  }
  
  // ファイルアップロード処理（複数ファイル対応）
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    try {
      const contents: string[] = []
      for (const file of Array.from(files)) {
        const text = await file.text()
        contents.push(`--- ${file.name} ---\n${text}`)
      }
      const combinedContent = contents.join('\n\n')
      setUploadedContent(combinedContent)
      handleChange('additionalSettings', (input.additionalSettings || '') + '\n\nアップロードファイル内容:\n' + combinedContent)
    } catch (error) {
      console.error('ファイル読み込みエラー:', error)
    }
  }
  
  // 複数キャラクター選択の処理
  const handleCharacterToggle = (characterId: string) => {
    const newIds = selectedCharacterIds.includes(characterId)
      ? selectedCharacterIds.filter(id => id !== characterId)
      : [...selectedCharacterIds, characterId]
    
    setSelectedCharacterIds(newIds)
    
    // 選択されたキャラクターのコンテキストを生成
    const selectedChars = characters.filter(c => newIds.includes(c.id))
    const characterContext = selectedChars.length > 0 
      ? selectedChars.map(c => generateCharacterContext(c)).join('\n\n')
      : undefined
    
    onChange({
      ...input,
      characterIds: newIds,
      characterContext: characterContext
    })
  }
  
  return (
    <div className="space-y-6">
      {/* 続き作成モードの表示 */}
      {input.continuationFrom?.previousContent && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <PenTool className="h-4 w-4 text-blue-600" />
            <p className="font-medium text-blue-900">続き作成モード</p>
          </div>
          <p className="text-sm text-blue-700">
            前回の文献から{input.continuationFrom.timeGap || '続き'}を作成します
          </p>
        </div>
      )}
      
      
      {/* 文献タイプ選択 */}
      <div>
        <Label>文献タイプ</Label>
        <DocumentTypeSelector
          value={input.documentType}
          onChange={(value) => {
            handleChange('documentType', value)
            // タイプ変更時にデフォルトスタイルを設定
            const defaultStyle = getDefaultStyle(value)
            onChange({
              ...input,
              documentType: value,
              visualStyle: {
                theme: defaultStyle
              }
            })
          }}
        />
      </div>
      
      {/* ビジュアルスタイル選択（対応タイプのみ表示） */}
      {(input.documentType === 'diary' || input.documentType === 'memo' || input.documentType === 'newspaper') && (
        <div>
          <Label className="flex items-center gap-2">
            <Palette className="w-4 h-4" />
            ビジュアルスタイル
          </Label>
          <Select
            value={input.visualStyle?.theme || getDefaultStyle(input.documentType)}
            onValueChange={(value) => onChange({
              ...input,
              visualStyle: {
                ...input.visualStyle,
                theme: value
              }
            })}
          >
            <SelectTrigger>
              <SelectValue placeholder="スタイルを選択..." />
            </SelectTrigger>
            <SelectContent>
              {getStylesForType(input.documentType).map(style => (
                <SelectItem key={style.id} value={style.id}>
                  <div>
                    <div className="font-medium">{style.name}</div>
                    <div className="text-xs text-gray-600">{style.description}</div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* 基本設定 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="genre">ジャンル</Label>
          <Select
            value={input.genre}
            onValueChange={(value) => handleChange('genre', value)}
          >
            <SelectTrigger id="genre">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fantasy">ファンタジー</SelectItem>
              <SelectItem value="sf">SF</SelectItem>
              <SelectItem value="modern">現代</SelectItem>
              <SelectItem value="historical">歴史</SelectItem>
              <SelectItem value="mystery">ミステリー</SelectItem>
              <SelectItem value="horror">ホラー</SelectItem>
              <SelectItem value="romance">ロマンス</SelectItem>
              <SelectItem value="comedy">コメディ</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="purpose">作成目的</Label>
          <Input
            id="purpose"
            value={input.purpose}
            onChange={(e) => handleChange('purpose', e.target.value)}
            placeholder="例: 事件の手がかり、世界観の説明"
          />
        </div>
      </div>
      
      {/* 内容設定 */}
      <div>
        <Label htmlFor="topic">主題・トピック（必須）</Label>
        <Textarea
          id="topic"
          value={input.topic || ''}
          onChange={(e) => handleChange('topic', e.target.value)}
          placeholder="文献の主題や含めるべき要素を入力してください"
          rows={3}
          required
        />
      </div>
      
      {/* ファイルアップロード */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
        <div className="text-center">
          <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-600 mb-2">テキストファイルをアップロード（複数選択可）</p>
          <input
            type="file"
            accept=".txt,.md"
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
            multiple
          />
          <Button 
            variant="outline" 
            size="sm" 
            className="cursor-pointer"
            onClick={() => document.getElementById('file-upload')?.click()}
            type="button"
          >
            ファイルを選択
          </Button>
          {uploadedContent && (
            <p className="text-xs text-green-600 mt-2">
              ファイルがアップロードされました
            </p>
          )}
        </div>
      </div>
      
      {/* 世界観連携 */}
      {savedWorlds.length > 0 && (
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="h-4 w-4" />
            <Label>世界観を選択（オプション）</Label>
          </div>
          <Select
            value={selectedWorldId || 'none'}
            onValueChange={(value) => {
              if (value === 'none') {
                setSelectedWorldId('')
                onChange({
                  ...input,
                  worldContext: undefined,
                  worldContextText: undefined
                })
              } else {
                setSelectedWorldId(value)
                const world = savedWorlds.find(w => w.id === value)
                if (world) {
                  const worldContextText = generateWorldContextText(world.result)
                  onChange({
                    ...input,
                    worldContext: {
                      worldId: world.id,
                      setting: world.result.summary,
                      era: world.result.details.era?.name,
                      culture: world.result.details.cultures?.map(c => c.description).join(', ')
                    },
                    worldContextText: worldContextText
                  })
                }
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="世界観を選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">選択しない</SelectItem>
              {savedWorlds.map(world => (
                <SelectItem key={world.id} value={world.id}>
                  {world.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* キャラクター連携（複数選択） */}
      {characters.length > 0 && (
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4" />
            <Label>関連キャラクター（複数選択可）</Label>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {characters.map(char => (
              <label key={char.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                <input
                  type="checkbox"
                  checked={selectedCharacterIds.includes(char.id)}
                  onChange={() => handleCharacterToggle(char.id)}
                  className="rounded"
                />
                <span className="flex-1">
                  {char.result.name} - {char.result.role || '役職不明'}
                </span>
              </label>
            ))}
          </div>
          {selectedCharacterIds.length > 0 && (
            <div className="mt-3 p-2 bg-blue-50 rounded text-sm">
              <p className="font-medium text-blue-900">
                選択中: {selectedCharacterIds.length}名
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* エネミー連携 */}
      {enemies.length > 0 && (
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sword className="h-4 w-4" />
            <Label>関連エネミー（オプション）</Label>
          </div>
          <Select
            value={selectedEnemyId || 'none'}
            onValueChange={(value) => {
              if (value === 'none') {
                setSelectedEnemyId('')
                onChange({
                  ...input,
                  enemyIds: [],
                  enemyContext: undefined
                })
              } else {
                setSelectedEnemyId(value)
                const enemy = enemies.find(e => e.id === value)
                if (enemy) {
                  onChange({
                    ...input,
                    enemyIds: [value],
                    enemyContext: generateEnemyContext(enemy)
                  })
                }
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="エネミーを選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">選択しない</SelectItem>
              {enemies.map(enemy => (
                <SelectItem key={enemy.id} value={enemy.id}>
                  {enemy.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* コミュニティ連携 */}
      {communities.length > 0 && (
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Home className="h-4 w-4" />
            <Label>関連コミュニティ（オプション）</Label>
          </div>
          <Select
            value={selectedCommunityId || 'none'}
            onValueChange={(value) => {
              if (value === 'none') {
                setSelectedCommunityId('')
                onChange({
                  ...input,
                  communityIds: [],
                  communityContext: undefined
                })
              } else {
                setSelectedCommunityId(value)
                const community = communities.find(c => c.id === value)
                if (community) {
                  onChange({
                    ...input,
                    communityIds: [value],
                    communityContext: generateCommunityContext(community)
                  })
                }
              }
              // 以前のrelatedData互換コードを削除
              if (false && value === 'none' && input.relatedData) {
                onChange({
                  ...input,
                  relatedData: {
                    ...input.relatedData,
                    communityId: undefined
                  }
                })
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="コミュニティを選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">選択しない</SelectItem>
              {communities.map(community => (
                <SelectItem key={community.id} value={community.id}>
                  {community.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* ソサエティ連携 */}
      {societies.length > 0 && (
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4" />
            <Label>関連ソサエティ（オプション）</Label>
          </div>
          <Select
            value={selectedSocietyId || 'none'}
            onValueChange={(value) => {
              if (value === 'none') {
                setSelectedSocietyId('')
                onChange({
                  ...input,
                  societyIds: [],
                  societyContext: undefined
                })
              } else {
                setSelectedSocietyId(value)
                const society = societies.find(s => s.id === value)
                if (society) {
                  onChange({
                    ...input,
                    societyIds: [value],
                    societyContext: generateSocietyContext(society)
                  })
                }
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="ソサエティを選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">選択しない</SelectItem>
              {societies.map(society => (
                <SelectItem key={society.id} value={society.id}>
                  {society.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* アイテム連携 */}
      {items.length > 0 && (
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Package className="h-4 w-4" />
            <Label>関連アイテム（オプション）</Label>
          </div>
          <Select
            value={selectedItemId || 'none'}
            onValueChange={(value) => {
              if (value === 'none') {
                setSelectedItemId('')
                onChange({
                  ...input,
                  itemIds: [],
                  itemContext: undefined
                })
              } else {
                setSelectedItemId(value)
                const item = items.find(i => i.id === value)
                if (item) {
                  onChange({
                    ...input,
                    itemIds: [value],
                    itemContext: generateItemContext(item)
                  })
                }
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="アイテムを選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">選択しない</SelectItem>
              {items.map(item => (
                <SelectItem key={item.id} value={item.id}>
                  {item.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* スキル連携 */}
      {skills.length > 0 && (
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4" />
            <Label>関連スキル（オプション）</Label>
          </div>
          <Select
            value={selectedSkillId || 'none'}
            onValueChange={(value) => {
              if (value === 'none') {
                setSelectedSkillId('')
                onChange({
                  ...input,
                  skillIds: []
                })
              } else {
                setSelectedSkillId(value)
                onChange({
                  ...input,
                  skillIds: [value]
                })
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="スキルを選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">選択しない</SelectItem>
              {skills.map(skill => (
                <SelectItem key={skill.id} value={skill.id}>
                  {skill.result.basicInfo?.name || 'スキル名不明'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* 建造物連携 */}
      {buildings.length > 0 && (
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Building className="h-4 w-4" />
            <Label>関連建造物（オプション）</Label>
          </div>
          <Select
            value={selectedBuildingId || 'none'}
            onValueChange={(value) => {
              if (value === 'none') {
                setSelectedBuildingId('')
                onChange({
                  ...input,
                  buildingIds: []
                })
              } else {
                setSelectedBuildingId(value)
                onChange({
                  ...input,
                  buildingIds: [value]
                })
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="建造物を選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">選択しない</SelectItem>
              {buildings.map(building => (
                <SelectItem key={building.id} value={building.id}>
                  {building.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* 詳細設定 */}
      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <Settings2 className="w-4 h-4" />
          <span>詳細設定</span>
          {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        {showAdvanced && (
          <div className="mt-4 space-y-4 border-t pt-4">
            {/* 文体設定 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="writingStyle">文体</Label>
                <Select
                  value={input.style?.writingStyle || 'auto'}
                  onValueChange={(value) => handleStyleChange('writingStyle', value)}
                >
                  <SelectTrigger id="writingStyle">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="formal">フォーマル</SelectItem>
                    <SelectItem value="informal">カジュアル</SelectItem>
                    <SelectItem value="academic">学術的</SelectItem>
                    <SelectItem value="journalistic">報道的</SelectItem>
                    <SelectItem value="personal">個人的</SelectItem>
                    <SelectItem value="technical">技術的</SelectItem>
                    <SelectItem value="creative">創造的</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="tone">トーン</Label>
                <Select
                  value={input.style?.tone || 'neutral'}
                  onValueChange={(value) => handleStyleChange('tone', value)}
                >
                  <SelectTrigger id="tone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="neutral">中立</SelectItem>
                    <SelectItem value="positive">ポジティブ</SelectItem>
                    <SelectItem value="negative">ネガティブ</SelectItem>
                    <SelectItem value="urgent">緊急</SelectItem>
                    <SelectItem value="mysterious">神秘的</SelectItem>
                    <SelectItem value="humorous">ユーモラス</SelectItem>
                    <SelectItem value="serious">真剣</SelectItem>
                    <SelectItem value="sensational">扇情的</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* 文字数指定 */}
            <div>
              <Label htmlFor="targetLength" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                目標文字数（オプション）
              </Label>
              <Input
                id="targetLength"
                type="number"
                min="10"
                max="30000"
                value={targetLength}
                onChange={(e) => handleLengthChange(e.target.value)}
                placeholder="10〜30000文字（空欄で自動）"
              />
              <p className="text-xs text-gray-500 mt-1">
                指定した文字数を目安に文献を生成します。空欄の場合は文献タイプに応じた適切な長さになります。
              </p>
            </div>

            {/* 追加設定 */}
            <div>
              <Label htmlFor="additionalSettings">追加の指示</Label>
              <Textarea
                id="additionalSettings"
                value={input.additionalSettings || ''}
                onChange={(e) => handleChange('additionalSettings', e.target.value)}
                placeholder="その他の詳細な指示があれば入力してください"
                rows={4}
              />
            </div>
            
            {/* 画像アップロード */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Image className="h-4 w-4" />
                <Label>画像アセット</Label>
              </div>
              
              {/* キャラクター画像 */}
              <ImageUploader
                type="avatar"
                documentType={input.documentType}
                avatars={input.imageAssets?.avatars}
                onAvatarUpload={handleAvatarUpload}
                onAvatarRemove={handleAvatarRemove}
              />
              
              {/* ヘッダー画像 */}
              <ImageUploader
                type="header"
                documentType={input.documentType}
                headerImage={input.imageAssets?.headerImage}
                onHeaderUpload={handleHeaderUpload}
                onHeaderRemove={handleHeaderRemove}
              />
            </div>
          </div>
        )}
      </div>
      
      {/* 生成ボタン */}
      <div className="flex justify-end">
        <Button
          onClick={() => {
            // 選択された関連データのコンテキストを収集
            let contextualData = ''
            
            // エネミー
            if (selectedEnemyId && selectedEnemyId !== '') {
              const enemy = enemies.find(e => e.id === selectedEnemyId)
              if (enemy) {
                contextualData += `\n\n【関連エネミー】\n${generateEnemyContext(enemy)}`
              }
            }
            
            // コミュニティ
            if (selectedCommunityId && selectedCommunityId !== '') {
              const community = communities.find(c => c.id === selectedCommunityId)
              if (community) {
                contextualData += `\n\n【関連コミュニティ】\n${generateCommunityContext(community)}`
              }
            }
            
            // ソサエティ
            if (selectedSocietyId && selectedSocietyId !== '') {
              const society = societies.find(s => s.id === selectedSocietyId)
              if (society) {
                contextualData += `\n\n【関連ソサエティ】\n${generateSocietyContext(society)}`
              }
            }
            
            // アイテム
            if (selectedItemId && selectedItemId !== '') {
              const item = items.find(i => i.id === selectedItemId)
              if (item) {
                contextualData += `\n\n【関連アイテム】\n${generateItemContext(item)}`
              }
            }
            
            // スキル
            if (selectedSkillId && selectedSkillId !== '') {
              const skill = skills.find(s => s.id === selectedSkillId)
              if (skill) {
                contextualData += `\n\n【関連スキル】\n${JSON.stringify(skill.result, null, 2)}`
              }
            }
            
            // 建造物
            if (selectedBuildingId && selectedBuildingId !== '') {
              const building = buildings.find(b => b.id === selectedBuildingId)
              if (building) {
                contextualData += `\n\n【関連建造物】\n${JSON.stringify(building.result, null, 2)}`
              }
            }
            
            // 関連データをrelatedDataに設定
            if (contextualData) {
              onChange({
                ...input,
                relatedData: {
                  enemyId: selectedEnemyId,
                  communityId: selectedCommunityId,
                  societyId: selectedSocietyId,
                  itemId: selectedItemId,
                  skillId: selectedSkillId,
                  buildingId: selectedBuildingId,
                  contextText: contextualData
                }
              })
              
              // 少し待ってから生成を実行（stateの更新を待つ）
              setTimeout(() => onGenerate(), 100)
            } else {
              onGenerate()
            }
          }}
          disabled={isGenerating || !input.topic}
          className="flex items-center gap-2"
        >
          <Sparkles className="w-4 h-4" />
          {isGenerating ? '生成中...' : '文献を生成'}
        </Button>
      </div>
    </div>
  )
}